/** The evaluator vocabulary the runner and this dashboard share. */

/**
 * How an evaluator reports: a judged score on the rubric, or a bare assertion.
 * The runner averages only the judged scores into `score`; assertions move
 * `passed` alone. So a case of assertions has no score to place on the ramp, and
 * a case can hold a 4.0 while failing.
 */
export const EvaluatorKind = {
  JUDGE: "judge",
  ASSERTION: "assertion",
} as const;

export type EvaluatorKind = (typeof EvaluatorKind)[keyof typeof EvaluatorKind];

/**
 * Every evaluator the runner can build, keyed by the name it posts results under
 * — the pydantic-evals evaluation name, which defaults to the evaluator's own
 * class name and is *not* the `expectationKey` flag that turns it on. Both
 * directions are recorded because the case form writes flags and the drawer reads
 * names.
 *
 * `max_latency_seconds` is the one `expectationKey` that is a column on the case
 * rather than an expectation. `presence` records how the runner tests for the
 * key: truthiness for the flags, non-null for the two value keys, which is what
 * makes `expected_dataframe_count: 0` a real assertion.
 */
export const EVALUATORS = {
  FigureEvaluator: {
    label: "Figure",
    kind: EvaluatorKind.JUDGE,
    expectationKey: "evaluate_figure",
    presence: "flag",
  },
  ValueEvaluator: {
    label: "Computed value",
    kind: EvaluatorKind.JUDGE,
    expectationKey: "evaluate_value",
    presence: "flag",
  },
  TextResponseEvaluator: {
    label: "Text response",
    kind: EvaluatorKind.JUDGE,
    expectationKey: "evaluate_text_response",
    presence: "flag",
  },
  ContainsTableEvaluator: {
    label: "Returns a table",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "evaluate_contains_table",
    presence: "flag",
  },
  SkillInvocationEvaluator: {
    label: "Skill invoked",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "evaluate_skill_invocation",
    presence: "flag",
  },
  ToolCallCountEvaluator: {
    label: "Tool call counts",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "expected_tool_call_counts",
    presence: "flag",
  },
  DataframeCountEvaluator: {
    label: "Dataframe count",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "expected_dataframe_count",
    presence: "value",
  },
  DataframeContentEvaluator: {
    label: "Dataframe content",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "expected_dataframe_content",
    presence: "flag",
  },
  LatencyEvaluator: {
    // Names the dimension, not the verdict: the row states the bound beside it.
    label: "Response time",
    kind: EvaluatorKind.ASSERTION,
    expectationKey: "max_latency_seconds",
    presence: "value",
  },
} as const satisfies Record<
  string,
  { label: string; kind: EvaluatorKind; expectationKey: string; presence: "flag" | "value" }
>;

/** The evaluator names, in the order a reviewer reads them: judges then assertions. */
export const EVALUATOR_ORDER = Object.keys(EVALUATORS) as (keyof typeof EVALUATORS)[];

/** The one evaluator that can state its own threshold, being a case column. */
export const LATENCY_EVALUATOR: keyof typeof EVALUATORS = "LatencyEvaluator";

/**
 * The judges a case can turn on from this form, cheapest question first: most
 * cases only want the text answer judged. Keyed off `EVALUATORS` so a form
 * toggle cannot name an expectation no evaluator reads.
 */
export const JUDGE_TOGGLES = [
  {
    key: EVALUATORS.TextResponseEvaluator.expectationKey,
    label: "Text response",
    hint: "Scores the written answer against the expected answer.",
  },
  {
    key: EVALUATORS.ValueEvaluator.expectationKey,
    label: "Computed value",
    hint: "Scores the number the agent arrived at, ignoring the prose around it.",
  },
  {
    key: EVALUATORS.FigureEvaluator.expectationKey,
    label: "Figure",
    hint: "Scores the rendered figure for correctness and legibility.",
  },
] as const;

/**
 * The assertions this form can express. `expected_tool_call_counts`,
 * `expected_dataframe_content` and `expected_table` are absent on purpose: they
 * are nested structures, and hand-typed JSON in a text box is a worse authoring
 * surface than the suite YAML the importer already reads.
 */
export const ASSERTION_TOGGLES = [
  {
    key: EVALUATORS.ContainsTableEvaluator.expectationKey,
    label: "Returns a table",
    hint: "Fails if the agent answered without producing a table.",
  },
  {
    key: EVALUATORS.SkillInvocationEvaluator.expectationKey,
    label: "Invokes a named skill",
    hint: "Fails if the agent reached for a different skill. Needs the skill named below.",
  },
] as const;
